import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, Trash2, CheckCircle2, XCircle, AlertCircle, RefreshCw, Link2, Copy, ExternalLink,
  Loader2, Clock, Zap, Key, Shield, Download
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { platformConfig, mapToOptions, defaultApiFields } from "@/components/integrations/platformConfig";
import { FacebookGuidedSetup } from "@/components/integrations/FacebookGuidedSetup";
import { SaleSmartlyGuidedSetup } from "@/components/integrations/SaleSmartlyGuidedSetup";
import { BrevoGuidedSetup } from "@/components/integrations/BrevoGuidedSetup";
import { MicrosoftLoginSetup } from "@/components/integrations/MicrosoftLoginSetup";
import { WhatsAppIntegrationSetup } from "@/components/integrations/WhatsAppIntegrationSetup";
import { TeamsIntegrationSetup } from "@/components/integrations/TeamsIntegrationSetup";
import { EngarageEndpointsSetup } from "@/components/integrations/EngarageEndpointsSetup";
import { getCodeSnippet, getSubscriptionSnippet } from "@/components/integrations/codeSnippets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IntegrationsPage = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [configSheetOpen, setConfigSheetOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [formData, setFormData] = useState({ name: "", credentials: {} });
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [codeSnippetLang, setCodeSnippetLang] = useState("javascript");
  const [regeneratingKey, setRegeneratingKey] = useState(false);
  const [apiFields, setApiFields] = useState([]);
  const [newField, setNewField] = useState({ variable_name: "", label: "", field_type: "text", map_to: "custom", required: true });

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/integrations`);
      setIntegrations(response.data);
    } catch (error) {
      toast.error("Failed to fetch integrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchIntegrations(); }, []);

  const openConfigSheet = async (integration) => {
    setSelectedIntegration(integration);
    setFormData({ name: integration.name, credentials: integration.credentials || {} });
    const savedFields = integration.credentials?.api_fields;
    setApiFields(savedFields && savedFields.length > 0 ? savedFields : defaultApiFields);
    setConfigSheetOpen(true);
    setCodeSnippetLang("javascript");

    try {
      const [webhookRes, logsRes] = await Promise.all([
        axios.get(`${API}/integrations/${integration.id}/webhook-url`),
        axios.get(`${API}/integrations/${integration.id}/logs`)
      ]);
      setWebhookInfo(webhookRes.data);
      setSyncLogs(logsRes.data);
    } catch (error) {
      console.error("Failed to fetch integration details", error);
    }
  };

  const handleCreate = async () => {
    if (!selectedPlatform || !formData.name) { toast.error("Please select a platform and enter a name"); return; }
    try {
      await axios.post(`${API}/integrations`, { platform: selectedPlatform, name: formData.name, credentials: formData.credentials });
      toast.success("Integration created successfully");
      setAddDialogOpen(false);
      fetchIntegrations();
    } catch (error) { toast.error("Failed to create integration"); }
  };

  const handleUpdate = async () => {
    if (!selectedIntegration) return;
    try {
      await axios.put(`${API}/integrations/${selectedIntegration.id}`, { name: formData.name, credentials: formData.credentials });
      toast.success("Integration updated successfully");
      fetchIntegrations();
    } catch (error) { toast.error("Failed to update integration"); }
  };

  const handleDelete = async (integrationId) => {
    if (!window.confirm("Are you sure you want to delete this integration?")) return;
    try {
      await axios.delete(`${API}/integrations/${integrationId}`);
      toast.success("Integration deleted successfully");
      setConfigSheetOpen(false);
      fetchIntegrations();
    } catch (error) { toast.error("Failed to delete integration"); }
  };

  const handleTest = async () => {
    if (!selectedIntegration) return;
    await handleUpdate();
    try {
      setTesting(true);
      const response = await axios.post(`${API}/integrations/${selectedIntegration.id}/test`);
      toast.success(response.data.message || "Connection successful!");
      fetchIntegrations();
    } catch (error) { toast.error(error.response?.data?.detail || "Connection test failed"); }
    finally { setTesting(false); }
  };

  const handleSync = async () => {
    if (!selectedIntegration) return;
    try {
      setSyncing(true);
      await axios.post(`${API}/integrations/${selectedIntegration.id}/sync`);
      toast.success("Sync started!");
      setTimeout(async () => {
        const logsRes = await axios.get(`${API}/integrations/${selectedIntegration.id}/logs`);
        setSyncLogs(logsRes.data);
        setSyncing(false);
      }, 3000);
    } catch (error) { toast.error(error.response?.data?.detail || "Sync failed"); setSyncing(false); }
  };

  const regenerateApiKey = async () => {
    if (!selectedIntegration || !window.confirm("Are you sure? This will invalidate the current API key.")) return;
    try {
      setRegeneratingKey(true);
      const response = await axios.post(`${API}/integrations/${selectedIntegration.id}/regenerate-key`);
      toast.success("API key regenerated successfully");
      setWebhookInfo(prev => ({ ...prev, api_key: response.data.api_key }));
      fetchIntegrations();
    } catch (error) { toast.error("Failed to regenerate API key"); }
    finally { setRegeneratingKey(false); }
  };

  const addApiField = () => {
    if (!newField.variable_name || !newField.label) { toast.error("Variable name and label are required"); return; }
    if (apiFields.some(f => f.variable_name === newField.variable_name)) { toast.error("Variable name already exists"); return; }
    setApiFields([...apiFields, { ...newField, id: Date.now().toString() }]);
    setNewField({ variable_name: "", label: "", field_type: "text", map_to: "custom", required: true });
    toast.success("Field added");
  };

  const removeApiField = (fieldId) => setApiFields(apiFields.filter(f => f.id !== fieldId));
  const updateApiField = (fieldId, key, value) => setApiFields(apiFields.map(f => f.id === fieldId ? { ...f, [key]: value } : f));

  const saveApiFields = async () => {
    if (!selectedIntegration) return;
    try {
      await axios.put(`${API}/integrations/${selectedIntegration.id}`, { credentials: { ...formData.credentials, api_fields: apiFields } });
      toast.success("Fields saved successfully");
      fetchIntegrations();
    } catch (error) { toast.error("Failed to save fields"); }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };

  const getStatusBadge = (status) => {
    switch (status) {
      case "connected": return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Connected</Badge>;
      case "error": return <Badge className="bg-red-100 text-red-700 border-red-200"><XCircle className="w-3 h-3 mr-1" /> Error</Badge>;
      default: return <Badge className="bg-gray-100 text-gray-700 border-gray-200"><AlertCircle className="w-3 h-3 mr-1" /> Disconnected</Badge>;
    }
  };

  const PlatformIcon = ({ platform, className = "w-5 h-5" }) => {
    const config = platformConfig[platform];
    if (!config) return null;
    const Icon = config.icon;
    return <Icon className={className} style={{ color: config.color }} />;
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto animate-fade-in" data-testid="integrations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Integrations</h1>
          <p className="text-sm text-gray-500 mt-1">Connect your platforms to automatically capture leads</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={async () => {
            try {
              const res = await axios.get(`${API}/admin/export-bson`, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const link = document.createElement('a'); link.href = url;
              link.setAttribute('download', `pomen_db_structure_${new Date().toISOString().slice(0,10)}.zip`);
              document.body.appendChild(link); link.click(); link.remove();
              toast.success("Database structure exported (no data)");
            } catch { toast.error("Failed to export database structure"); }
          }} className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" data-testid="download-bson-btn">
            <Download className="w-4 h-4 mr-2" /> Download BSON
          </Button>
          <Button size="sm" onClick={() => { setSelectedPlatform(""); setFormData({ name: "", credentials: {} }); setAddDialogOpen(true); }} className="bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg" data-testid="add-integration-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Integration
          </Button>
        </div>
      </div>

      {/* Integration Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[1, 2, 3, 4].map(i => <Card key={i} className="border border-gray-200 rounded-xl"><CardContent className="p-6"><div className="h-24 bg-gray-100 rounded-lg animate-pulse" /></CardContent></Card>)}
        </div>
      ) : integrations.length === 0 ? (
        <Card className="border border-gray-200 rounded-xl bg-white">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center"><Link2 className="w-8 h-8 text-gray-400" /></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No integrations yet</h3>
            <p className="text-sm text-gray-500 mb-6">Connect your platforms to automatically capture leads</p>
            <Button onClick={() => { setSelectedPlatform(""); setFormData({ name: "", credentials: {} }); setAddDialogOpen(true); }} className="bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg">
              <Plus className="w-4 h-4 mr-2" /> Add Your First Integration
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {integrations.map(integration => {
            const config = platformConfig[integration.platform];
            if (!config) return null;
            return (
              <Card key={integration.id} className="border border-gray-200 rounded-xl bg-white hover:border-gray-300 transition-colors cursor-pointer" onClick={() => openConfigSheet(integration)} data-testid={`integration-${integration.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: config.bgColor }}>
                        <PlatformIcon platform={integration.platform} className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{integration.name}</h3>
                        <p className="text-sm text-gray-500">{config.name}</p>
                      </div>
                    </div>
                    {getStatusBadge(integration.status)}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4 text-gray-500">
                      <span className="flex items-center gap-1" data-testid={`integration-logs-count-${integration.id}`}><Zap className="w-4 h-4" />{integration.logs_count || 0} logs</span>
                      {integration.last_sync && <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{format(new Date(integration.last_sync), "MMM d, HH:mm")}</span>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          <Card className="border border-dashed border-gray-300 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" onClick={() => { setSelectedPlatform(""); setFormData({ name: "", credentials: {} }); setAddDialogOpen(true); }}>
            <CardContent className="p-6 flex items-center justify-center min-h-[140px]">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-2 bg-gray-200 rounded-lg flex items-center justify-center"><Plus className="w-5 h-5 text-gray-500" /></div>
                <p className="text-sm font-medium text-gray-600">Add Integration</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">Add Integration</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">Choose a platform to connect</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-gray-500">Platform</Label>
              <div className="grid grid-cols-3 gap-3" data-testid="platform-picker-grid">
                {Object.entries(platformConfig).map(([key, config]) => {
                  const isActive = selectedPlatform === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedPlatform(key)}
                      data-testid={`platform-option-${key}`}
                      className={`group relative flex flex-col items-center gap-2 px-3 py-4 rounded-xl border-2 transition-all text-left ${
                        isActive
                          ? "border-[#0D0D5B] bg-[#0D0D5B]/5 shadow-sm"
                          : "border-gray-200 hover:border-[#0D0D5B]/40 hover:bg-gray-50"
                      }`}
                    >
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: config.bgColor || "#F3F4F6" }}
                      >
                        <PlatformIcon platform={key} className="w-5 h-5" />
                      </div>
                      <span className={`text-xs font-medium leading-tight text-center ${isActive ? "text-[#0D0D5B]" : "text-gray-700"}`}>
                        {config.name}
                      </span>
                      {isActive && (
                        <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-[#0D0D5B] text-white flex items-center justify-center" data-testid={`platform-selected-${key}`}>
                          <CheckCircle2 className="w-3 h-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            {selectedPlatform && (
              <div className="space-y-2">
                <Label>Integration Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={`My ${platformConfig[selectedPlatform].name}`} className="border-gray-300 rounded-lg" data-testid="integration-name-input" />
                <p className="text-xs text-gray-500">{platformConfig[selectedPlatform].description}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="border-gray-300 rounded-lg">Cancel</Button>
            <Button onClick={handleCreate} disabled={!selectedPlatform || !formData.name} className="bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg" data-testid="create-integration-btn">Create Integration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Sheet */}
      <Sheet open={configSheetOpen} onOpenChange={setConfigSheetOpen}>
        <SheetContent className="w-[920px] sm:max-w-[920px] overflow-y-auto">
          {selectedIntegration && platformConfig[selectedIntegration.platform] && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: platformConfig[selectedIntegration.platform].bgColor }}>
                    <PlatformIcon platform={selectedIntegration.platform} className="w-5 h-5" />
                  </div>
                  <div>
                    <SheetTitle className="text-lg font-semibold text-gray-900">{selectedIntegration.name}</SheetTitle>
                    <SheetDescription className="text-sm text-gray-500">{platformConfig[selectedIntegration.platform].name}</SheetDescription>
                  </div>
                </div>
              </SheetHeader>

              <Tabs defaultValue={selectedIntegration.platform === "api" ? "api-fields" : selectedIntegration.platform === "engarage" ? "engarage-endpoints" : "settings"} className="mt-6">
                <TabsList className={`grid w-full ${selectedIntegration.platform === "api" ? "grid-cols-4" : selectedIntegration.platform === "engarage" ? "grid-cols-3" : selectedIntegration.platform === "whatsapp" ? "grid-cols-4" : "grid-cols-3"}`}>
                  {selectedIntegration.platform === "api" ? (
                    <><TabsTrigger value="api-fields">Fields</TabsTrigger><TabsTrigger value="api-code">Code</TabsTrigger><TabsTrigger value="api-key">API Key</TabsTrigger><TabsTrigger value="logs">Logs</TabsTrigger></>
                  ) : selectedIntegration.platform === "engarage" ? (
                    <><TabsTrigger value="engarage-endpoints" data-testid="eng-tab-endpoints">Endpoints</TabsTrigger><TabsTrigger value="api-key" data-testid="eng-tab-key">API Key</TabsTrigger><TabsTrigger value="logs" data-testid="eng-tab-logs">Logs</TabsTrigger></>
                  ) : selectedIntegration.platform === "whatsapp" ? (
                    <><TabsTrigger value="settings" data-testid="wa-top-tab-settings">Settings</TabsTrigger><TabsTrigger value="webhook" data-testid="wa-top-tab-webhook">Webhook</TabsTrigger><TabsTrigger value="templates" data-testid="wa-top-tab-templates">Templates</TabsTrigger><TabsTrigger value="logs" data-testid="wa-top-tab-logs">Logs</TabsTrigger></>
                  ) : (
                    <><TabsTrigger value="settings">Settings</TabsTrigger><TabsTrigger value="webhook">Webhook</TabsTrigger><TabsTrigger value="logs">Logs</TabsTrigger></>
                  )}
                </TabsList>

                {/* API Fields Tab */}
                {selectedIntegration.platform === "api" && (
                  <TabsContent value="api-fields" className="space-y-4 mt-4">
                    <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">Define fields to capture. Code snippet auto-generates based on these.</div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Form Fields</Label>
                      {apiFields.map(field => (
                        <div key={field.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <div className="flex-1 grid grid-cols-4 gap-2">
                            <Input value={field.variable_name} onChange={(e) => updateApiField(field.id, 'variable_name', e.target.value)} className="h-8 text-xs" placeholder="variable_name" />
                            <Input value={field.label} onChange={(e) => updateApiField(field.id, 'label', e.target.value)} className="h-8 text-xs" placeholder="Label" />
                            <Select value={field.map_to} onValueChange={(v) => updateApiField(field.id, 'map_to', v)}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>{mapToOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <div className="flex gap-1">
                              <Select value={field.required ? "yes" : "no"} onValueChange={(v) => updateApiField(field.id, 'required', v === "yes")}>
                                <SelectTrigger className="h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="yes">Req</SelectItem><SelectItem value="no">Opt</SelectItem></SelectContent>
                              </Select>
                              <Button variant="ghost" size="sm" onClick={() => removeApiField(field.id)} className="h-8 w-8 p-0 text-gray-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="border-t pt-4">
                      <Label className="text-sm mb-2 block">Add Field</Label>
                      <div className="flex gap-2 p-2 bg-gray-50 rounded-lg">
                        <Input value={newField.variable_name} onChange={(e) => setNewField({ ...newField, variable_name: e.target.value.replace(/\s/g, '_').toLowerCase() })} className="h-8 text-xs flex-1" placeholder="variable_name" />
                        <Input value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} className="h-8 text-xs flex-1" placeholder="Label" />
                        <Select value={newField.map_to} onValueChange={(v) => setNewField({ ...newField, map_to: v })}>
                          <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                          <SelectContent>{mapToOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" onClick={addApiField} className="h-8 px-2 bg-[#0D0D5B]"><Plus className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <Button onClick={saveApiFields} className="w-full bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg">Save Fields</Button>
                  </TabsContent>
                )}

                {/* API Code Tab */}
                {selectedIntegration.platform === "api" && webhookInfo && (
                  <TabsContent value="api-code" className="space-y-4 mt-4">
                    {selectedIntegration.name?.includes("Subscription") ? (
                      <>
                        <div className="flex items-center justify-between">
                          <Label>Subscription Update Code Snippet</Label>
                          <Select value={codeSnippetLang} onValueChange={setCodeSnippetLang}>
                            <SelectTrigger className="w-32 h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="javascript">JavaScript</SelectItem><SelectItem value="curl">cURL</SelectItem><SelectItem value="python">Python</SelectItem><SelectItem value="php">PHP</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div className="relative">
                          <pre className="text-xs bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto max-h-[350px]"><code>{getSubscriptionSnippet(codeSnippetLang, webhookInfo.api_key, webhookInfo.webhook_url)}</code></pre>
                          <Button variant="outline" size="sm" onClick={() => copyToClipboard(getSubscriptionSnippet(codeSnippetLang, webhookInfo.api_key, webhookInfo.webhook_url))} className="absolute top-2 right-2 h-7 bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                        </div>
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                          <strong>Embed this in ENGARAGE's subscription module.</strong> Call after a user subscribes to auto-update the lead to "Subscribed" with plan info (Basic, Plus, or Pro).
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2 text-sm">Accepted Fields</h4>
                          <div className="flex flex-wrap gap-1">
                            {[{k:"workshop_id",r:true},{k:"email",r:true},{k:"plan",r:true},{k:"subscriber_name",r:false},{k:"company",r:false},{k:"subscription_id",r:false},{k:"subscription_date",r:false}].map(f => (
                              <Badge key={f.k} variant="outline" className="text-xs">{f.k}{f.r && <span className="text-red-500 ml-1">*</span>}</Badge>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Plan values: <code className="bg-gray-200 px-1 rounded">Basic</code> <code className="bg-gray-200 px-1 rounded">Plus</code> <code className="bg-gray-200 px-1 rounded">Pro</code></p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <Label>Auto-Generated Code ({apiFields.length} fields)</Label>
                          <Select value={codeSnippetLang} onValueChange={setCodeSnippetLang}>
                            <SelectTrigger className="w-32 h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="javascript">JavaScript</SelectItem><SelectItem value="curl">cURL</SelectItem><SelectItem value="python">Python</SelectItem><SelectItem value="php">PHP</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div className="relative">
                          <pre className="text-xs bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto max-h-[350px]"><code>{getCodeSnippet(codeSnippetLang, webhookInfo.api_key, webhookInfo.webhook_url, apiFields, defaultApiFields)}</code></pre>
                          <Button variant="outline" size="sm" onClick={() => copyToClipboard(getCodeSnippet(codeSnippetLang, webhookInfo.api_key, webhookInfo.webhook_url, apiFields, defaultApiFields))} className="absolute top-2 right-2 h-7 bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2 text-sm">Fields Included</h4>
                          <div className="flex flex-wrap gap-1">{apiFields.map(f => <Badge key={f.id} variant="outline" className="text-xs">{f.variable_name} → {f.map_to}{f.required && <span className="text-red-500 ml-1">*</span>}</Badge>)}</div>
                        </div>
                      </>
                    )}
                  </TabsContent>
                )}

                {/* ENGARAGE Endpoints Tab */}
                {selectedIntegration.platform === "engarage" && (
                  <TabsContent value="engarage-endpoints" className="space-y-4 mt-4">
                    <EngarageEndpointsSetup integration={selectedIntegration} />
                  </TabsContent>
                )}

                {/* API Key Tab — reused by api and engarage */}
                {(selectedIntegration.platform === "api" || selectedIntegration.platform === "engarage") && webhookInfo && (
                  <TabsContent value="api-key" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Integration Name</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="border-gray-300 rounded-lg" />
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                      <div>
                        <Label className="text-xs text-gray-500 flex items-center gap-1"><Key className="w-3 h-3" /> API Key</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="flex-1 text-sm bg-white px-3 py-2 rounded border border-gray-200 font-mono">{webhookInfo.api_key}</code>
                          <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.api_key)}><Copy className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">API Endpoint</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="flex-1 text-xs bg-white px-3 py-2 rounded border border-gray-200 font-mono">{webhookInfo.webhook_url}</code>
                          <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.webhook_url)}><Copy className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" onClick={regenerateApiKey} disabled={regeneratingKey} className="w-full border-orange-200 text-orange-600 hover:bg-orange-50">
                      {regeneratingKey ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Regenerate API Key
                    </Button>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={handleTest} disabled={testing} className="flex-1">{testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Test</Button>
                      <Button onClick={handleUpdate} className="flex-1 bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white">Save</Button>
                    </div>
                  </TabsContent>
                )}

                {/* Settings Tab — platform-specific guided setups */}
                {selectedIntegration.platform === "facebook" && (
                  <TabsContent value="settings" className="space-y-4 mt-4">
                    <FacebookGuidedSetup
                      integration={selectedIntegration}
                      formData={formData}
                      setFormData={setFormData}
                      webhookInfo={webhookInfo}
                      handleUpdate={handleUpdate}
                      handleTest={handleTest}
                      testing={testing}
                      copyToClipboard={copyToClipboard}
                    />
                  </TabsContent>
                )}

                {selectedIntegration.platform === "salesmartly" && (
                  <TabsContent value="settings" className="space-y-4 mt-4">
                    <SaleSmartlyGuidedSetup
                      integration={selectedIntegration}
                      formData={formData}
                      setFormData={setFormData}
                      webhookInfo={webhookInfo}
                      handleUpdate={handleUpdate}
                      handleTest={handleTest}
                      testing={testing}
                      copyToClipboard={copyToClipboard}
                    />
                  </TabsContent>
                )}

                {selectedIntegration.platform === "brevo" && (
                  <TabsContent value="settings" className="space-y-4 mt-4">
                    <BrevoGuidedSetup
                      integration={selectedIntegration}
                      formData={formData}
                      setFormData={setFormData}
                      webhookInfo={webhookInfo}
                      handleUpdate={handleUpdate}
                      handleTest={handleTest}
                      testing={testing}
                      copyToClipboard={copyToClipboard}
                    />
                  </TabsContent>
                )}

                {selectedIntegration.platform === "teams" && (
                  <TabsContent value="settings" className="space-y-4 mt-4">
                    <TeamsIntegrationSetup integration={selectedIntegration} />
                  </TabsContent>
                )}

                {selectedIntegration.platform === "whatsapp" && (
                  <TabsContent value="settings" className="space-y-4 mt-4">
                    <WhatsAppIntegrationSetup integration={selectedIntegration} mode="settings" />
                  </TabsContent>
                )}

                {selectedIntegration.platform === "whatsapp" && (
                  <TabsContent value="templates" className="space-y-4 mt-4">
                    <WhatsAppIntegrationSetup integration={selectedIntegration} mode="templates" />
                  </TabsContent>
                )}

                {selectedIntegration.platform === "microsoft_login" && (
                  <TabsContent value="settings" className="space-y-4 mt-4">
                    <MicrosoftLoginSetup integration={selectedIntegration} />
                  </TabsContent>
                )}

                {/* Generic Settings Tab — other platforms */}
                {selectedIntegration.platform !== "api" && selectedIntegration.platform !== "facebook" && selectedIntegration.platform !== "salesmartly" && selectedIntegration.platform !== "brevo" && selectedIntegration.platform !== "teams" && selectedIntegration.platform !== "whatsapp" && selectedIntegration.platform !== "microsoft_login" && (
                  <TabsContent value="settings" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Integration Name</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="border-gray-300 rounded-lg" />
                    </div>
                    {platformConfig[selectedIntegration.platform].fields.map(field => (
                      <div key={field.key} className="space-y-2">
                        <Label>{field.label}{field.required && <span className="text-red-500 ml-1">*</span>}</Label>
                        <Input type={field.type} value={formData.credentials[field.key] || ""} onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, [field.key]: e.target.value } })} placeholder={field.help} className="border-gray-300 rounded-lg" />
                      </div>
                    ))}
                    {platformConfig[selectedIntegration.platform].docs && <a href={platformConfig[selectedIntegration.platform].docs} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">Docs <ExternalLink className="w-3 h-3" /></a>}
                    <div className="flex gap-2 pt-4">
                      <Button variant="outline" onClick={handleTest} disabled={testing} className="flex-1">{testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Test Connection</Button>
                      <Button onClick={handleUpdate} className="flex-1 bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white">Save Changes</Button>
                    </div>
                  </TabsContent>
                )}

                {/* Webhook Tab — Facebook Guided */}
                {selectedIntegration.platform === "facebook" && webhookInfo && (
                  <TabsContent value="webhook" className="space-y-4 mt-4">
                    <div className="p-4 bg-[#1877F2]/5 rounded-xl border border-[#1877F2]/20 space-y-4">
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2"><Shield className="w-4 h-4 text-[#1877F2]" /> Webhook Credentials</h4>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 block">Callback URL (paste into Facebook)</Label>
                          <div className="flex gap-2"><code className="flex-1 text-xs bg-white px-3 py-2 rounded-lg border border-gray-200 font-mono break-all">{webhookInfo.webhook_url}</code><Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.webhook_url)} className="shrink-0"><Copy className="w-4 h-4" /></Button></div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 block">Verify Token (paste into Facebook)</Label>
                          <div className="flex gap-2"><code className="flex-1 text-xs bg-white px-3 py-2 rounded-lg border border-gray-200 font-mono">{webhookInfo.verify_token}</code><Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.verify_token)} className="shrink-0"><Copy className="w-4 h-4" /></Button></div>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                      These credentials are auto-generated by POMEN. You need them for Step 3 in the Setup tab.
                    </div>
                  </TabsContent>
                )}

                {/* Webhook Tab — SaleSmartly */}
                {selectedIntegration.platform === "salesmartly" && webhookInfo && (
                  <TabsContent value="webhook" className="space-y-4 mt-4">
                    <div className="p-4 bg-[#0868F7]/5 rounded-xl border border-[#0868F7]/20 space-y-4">
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2"><Shield className="w-4 h-4 text-[#0868F7]" /> Webhook Credentials</h4>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 block">Callback URL (paste into SaleSmartly)</Label>
                          <div className="flex gap-2"><code className="flex-1 text-xs bg-white px-3 py-2 rounded-lg border border-gray-200 font-mono break-all">{webhookInfo.webhook_url}</code><Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.webhook_url)} className="shrink-0"><Copy className="w-4 h-4" /></Button></div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 block">Verify Token</Label>
                          <div className="flex gap-2"><code className="flex-1 text-xs bg-white px-3 py-2 rounded-lg border border-gray-200 font-mono">{webhookInfo.verify_token}</code><Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.verify_token)} className="shrink-0"><Copy className="w-4 h-4" /></Button></div>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                      Paste the Callback URL into SaleSmartly: <strong>Settings → Project Management → Enterprise Development Configuration</strong>
                    </div>
                  </TabsContent>
                )}

                {/* Webhook Tab — Brevo */}
                {selectedIntegration.platform === "brevo" && webhookInfo && (
                  <TabsContent value="webhook" className="space-y-4 mt-4">
                    <div className="p-4 rounded-xl space-y-4" style={{ background: "#0B996E0D", border: "1px solid #0B996E26" }}>
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2"><Shield className="w-4 h-4" style={{ color: "#0B996E" }} /> Webhook Credentials</h4>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 block">Callback URL (paste into Brevo)</Label>
                          <div className="flex gap-2"><code className="flex-1 text-xs bg-white px-3 py-2 rounded-lg border border-gray-200 font-mono break-all">{webhookInfo.webhook_url}</code><Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.webhook_url)} className="shrink-0"><Copy className="w-4 h-4" /></Button></div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 block">Verify Token (header <code className="bg-white px-1 rounded border">x-pomen-token</code>)</Label>
                          <div className="flex gap-2"><code className="flex-1 text-xs bg-white px-3 py-2 rounded-lg border border-gray-200 font-mono">{webhookInfo.verify_token}</code><Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.verify_token)} className="shrink-0"><Copy className="w-4 h-4" /></Button></div>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                      Paste the Callback URL into Brevo: <strong>Automation → Transactional → Settings → Webhook</strong>, then add the custom header <code className="bg-white px-1 rounded border">x-pomen-token</code> with the Verify Token above.
                    </div>
                  </TabsContent>
                )}

                {/* Webhook Tab — Other platforms */}
                {selectedIntegration.platform !== "api" && selectedIntegration.platform !== "facebook" && selectedIntegration.platform !== "salesmartly" && selectedIntegration.platform !== "brevo" && webhookInfo && (
                  <TabsContent value="webhook" className="space-y-4 mt-4">
                    <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                      <div><Label className="text-xs text-gray-500">Webhook URL</Label><div className="flex gap-2 mt-1"><code className="flex-1 text-sm bg-white px-3 py-2 rounded border overflow-x-auto">{webhookInfo.webhook_url}</code><Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.webhook_url)}><Copy className="w-4 h-4" /></Button></div></div>
                      <div><Label className="text-xs text-gray-500">Verify Token</Label><div className="flex gap-2 mt-1"><code className="flex-1 text-sm bg-white px-3 py-2 rounded border">{webhookInfo.verify_token}</code><Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.verify_token)}><Copy className="w-4 h-4" /></Button></div></div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                      <p className="font-medium mb-1">Setup:</p>
                      <ol className="list-decimal list-inside space-y-1"><li>Copy the Webhook URL</li><li>Add it to your platform's developer settings</li><li>Use the Verify Token for verification</li></ol>
                    </div>
                  </TabsContent>
                )}

                {/* Logs Tab */}
                <TabsContent value="logs" className="space-y-4 mt-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium text-gray-900">Recent Activity</h4>
                    {selectedIntegration.platform !== "api" && selectedIntegration.platform !== "engarage" && selectedIntegration.platform !== "teams" && selectedIntegration.platform !== "microsoft_login" && <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing || selectedIntegration.status !== "connected"}>{syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Manual Sync</Button>}
                  </div>
                  {syncLogs.length === 0 ? <p className="text-sm text-gray-500 text-center py-8">No activity yet.</p> : (
                    <div className="space-y-2">{syncLogs.map(log => {
                      const typeLabel = {
                        manual: "Manual Sync",
                        api: "API",
                        webhook: "Webhook",
                        schedule_meeting: "Meeting Scheduled",
                        oauth_callback: "Login Attempt",
                      }[log.sync_type] || log.sync_type;
                      return (
                        <div key={log.id} className="p-3 bg-gray-50 rounded-lg" data-testid={`log-entry-${log.id}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900">{typeLabel}</span>
                            <Badge className={log.status === "success" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>{log.status}</Badge>
                          </div>
                          {log.message && <p className="text-xs text-gray-700 mb-1 break-words">{log.message}</p>}
                          <div className="text-xs text-gray-500">{format(new Date(log.started_at), "MMM d, yyyy HH:mm:ss")}{log.leads_created > 0 && <span className="ml-2 text-emerald-600">+{log.leads_created} leads</span>}</div>
                        </div>
                      );
                    })}</div>
                  )}
                </TabsContent>
              </Tabs>

              <div className="mt-6 pt-4 border-t"><Button variant="outline" onClick={() => handleDelete(selectedIntegration.id)} className="text-red-600 border-red-200 hover:bg-red-50 rounded-lg w-full"><Trash2 className="w-4 h-4 mr-2" /> Delete Integration</Button></div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default IntegrationsPage;
